/**
 * Shared frontend tool schemas.
 *
 * Imported by both unthread/backend (so the Copilot agent's `buildPageTools`
 * can register the right schemas) and unthread/frontend (so `WebMcpService`
 * can register the same tools on `navigator.modelContext`).
 *
 * Adding a new shared schema here is the canonical way to keep a tool's name
 * and input shape aligned across the BE agent loop and the FE WebMCP registry.
 */

/** Generic JSON Schema shape for tool input. Matches the WebMCP / MCP spec. */
export interface ToolInputSchema {
  type: 'object';
  properties?: Record<string, unknown>;
  required?: string[];
  [extra: string]: unknown;
}

/** A frontend tool — name, description, input schema. The execute body lives in the consumer. */
export interface FrontendToolDefinition<TInput = unknown> {
  name: string;
  description: string;
  inputSchema: ToolInputSchema;
  /** Phantom type for input shape (not present at runtime). */
  readonly _input?: TInput;
}

// ============================================================================
// App-wide action names
// ============================================================================
//
// Action names used by BE → FE `invoke-fe-tool` dispatch that aren't registered
// as WebMCP tools (no callable surface, no input schema). Lives in the shared
// module so a rename catches at compile time on both sides.

/** App-wide navigation action — handled by AppComponent, dispatched by both
 * the BE Copilot's `run_navigation_assistant` and WebMCP's `navigation_assistant`. */
export const NAVIGATE_ACTION_NAME = 'navigate';

// ============================================================================
// apply_inbox_filters
// ============================================================================

/**
 * Structured filter clause applied to the inbox view. Mirrors the backend's
 * WhereClause shape but expressed in plain JSON-friendly form so the schema
 * is portable across the BE/FE boundary without a type dependency.
 */
export interface InboxFilterClause {
  field: string;
  operator: string;
  value: unknown;
}

export interface ApplyInboxFiltersInput {
  filters: InboxFilterClause[];
  /** Optional human-readable labels for filter values; used for UI display. */
  resolvedEntityLabels?: Record<string, string>;
}

export const APPLY_INBOX_FILTERS_TOOL: FrontendToolDefinition<ApplyInboxFiltersInput> = {
  name: 'apply_inbox_filters',
  description:
    'Apply structured filters to the staff member\'s inbox view. Use after resolving filter entity IDs (assignee, tags, etc.). Replaces all current filters with the provided set.',
  inputSchema: {
    type: 'object',
    properties: {
      filters: {
        type: 'array',
        description: 'Filter clauses to apply.',
        items: {
          type: 'object',
          properties: {
            field: { type: 'string', description: 'Filter field id (e.g. "assignedToUserId").' },
            operator: { type: 'string', description: 'Filter operator (e.g. "in", "eq").' },
            value: { description: 'Filter value (string, array, or other field-specific shape).' },
          },
          required: ['field', 'operator'],
        },
      },
      resolvedEntityLabels: {
        type: 'object',
        description: 'Optional map of value→human-readable label for UI display.',
        additionalProperties: { type: 'string' },
      },
    },
    required: ['filters'],
  },
};

// ============================================================================
// insert_draft_response
// ============================================================================

export interface ReplyComposerRewriteContext {
  inline: boolean;
  html: string;
  text: string;
  from: number;
  to: number;
}

export interface InsertDraftResponseInput {
  /** HTML content to insert into the conversation's reply composer. */
  html: string;
  /** Replace a captured range only if the conversation and draft still match. */
  replace?: {
    conversationId: string;
    originalHtml: string;
    from: number;
    to: number;
  };
}

export const INSERT_DRAFT_RESPONSE_TOOL: FrontendToolDefinition<InsertDraftResponseInput> = {
  name: 'insert_draft_response',
  description:
    'Insert HTML content into the active conversation\'s reply composer. Only valid on a conversation-details page. Use replace to rewrite a captured range, with originalHtml to detect draft changes. The composer is left in draft state; the user reviews and sends manually.',
  inputSchema: {
    type: 'object',
    properties: {
      html: { type: 'string', description: 'HTML content to insert.' },
      replace: {
        type: 'object',
        properties: {
          conversationId: { type: 'string' },
          originalHtml: { type: 'string' },
          from: { type: 'integer', minimum: 0 },
          to: { type: 'integer', minimum: 0 },
        },
        required: ['conversationId', 'originalHtml', 'from', 'to'],
      },
    },
    required: ['html'],
  },
};

// ============================================================================
// apply_automation_change
// ============================================================================

export interface ApplyAutomationChangeInput {
  /** Updated automation document — replaces the current automation under edit. */
  automationData: Record<string, unknown>;
}

export const APPLY_AUTOMATION_CHANGE_TOOL: FrontendToolDefinition<ApplyAutomationChangeInput> = {
  name: 'apply_automation_change',
  description:
    'Replace the automation currently being edited with a new automation document. Only valid on an automation-edit page.',
  inputSchema: {
    type: 'object',
    properties: {
      automationData: {
        type: 'object',
        description: 'New automation document (structure mirrors the automation editor\'s internal model).',
      },
    },
    required: ['automationData'],
  },
};

// ============================================================================
// replace_selected_text
// ============================================================================

export interface ReplaceSelectedTextInput {
  /** Replacement text — replaces the editor\'s current selection. */
  rewrittenText: string;
}

export const REPLACE_SELECTED_TEXT_TOOL: FrontendToolDefinition<ReplaceSelectedTextInput> = {
  name: 'replace_selected_text',
  description:
    'Replace the current text selection in the knowledge-base article editor with rewritten content. Only valid on a knowledge-article-edit page with an active text selection.',
  inputSchema: {
    type: 'object',
    properties: {
      rewrittenText: { type: 'string', description: 'Replacement text.' },
    },
    required: ['rewrittenText'],
  },
};

// ============================================================================
// insert_text_at_cursor
// ============================================================================

export interface InsertTextAtCursorInput {
  /** Markdown text to insert at the editor cursor. */
  markdownText: string;
  /** Optional cursor position from the context snapshot used to generate the text. */
  cursorPosition?: number;
  /** Revision fingerprint of the editor at proposal time — for drift detection. */
  editorRevision?: string;
}

export const INSERT_TEXT_AT_CURSOR_TOOL: FrontendToolDefinition<InsertTextAtCursorInput> = {
  name: 'insert_text_at_cursor',
  description:
    'Insert Markdown text at the current cursor in the knowledge-base article editor as a reviewable diff. Only valid on a knowledge-article-edit page with a cursor location.',
  inputSchema: {
    type: 'object',
    properties: {
      markdownText: { type: 'string', description: 'Markdown text to insert at the cursor.' },
      cursorPosition: {
        type: 'number',
        description: 'Optional cursor position from the context snapshot used to generate the text.',
      },
      editorRevision: { type: 'string', description: 'Editor revision fingerprint at proposal time.' },
    },
    required: ['markdownText'],
  },
};

// ============================================================================
// create_kb_article_draft
// ============================================================================

export interface RelatedKbArticleRef {
  id: string;
  title: string;
  url: string | null;
}

export interface CreateKbArticleDraftInput {
  /** Existing draft article id created server-side. Always present on the BE-driven flow. */
  articleId: string;
  /** Project the article belongs to. Null for tenant-wide articles. */
  projectId: string | null;
  /** Article title (used for fallback creation + UI). */
  title: string;
  /** Full draft markdown content. */
  content: string;
  /** Discriminator for how the BE chose to draft (source_evidence vs gap-only). */
  sourceMode?: string;
  sourceSummary?: string;
  rationale?: string;
  warnings?: string[];
  sourceGapIds?: string[];
  sourceConversationIds?: string[];
  relatedArticles?: RelatedKbArticleRef[];
  generatedFromGapProposal?: boolean;
}

export const CREATE_KB_ARTICLE_DRAFT_TOOL: FrontendToolDefinition<CreateKbArticleDraftInput> = {
  name: 'create_kb_article_draft',
  description:
    'Open a freshly-drafted knowledge-base article in the editor. The BE creates the draft and returns its id; the FE navigates to the editor and confirms via toast. App-wide — works from any page.',
  inputSchema: {
    type: 'object',
    properties: {
      articleId: { type: 'string', description: 'Server-assigned draft article id to navigate to.' },
      projectId: { type: ['string', 'null'], description: 'Project the article belongs to, or null for tenant-wide.' },
      title: { type: 'string', description: 'Article title.' },
      content: { type: 'string', description: 'Draft markdown content (informational; the BE already persisted it).' },
      sourceMode: { type: 'string' },
      sourceSummary: { type: 'string' },
      rationale: { type: 'string' },
      warnings: { type: 'array', items: { type: 'string' } },
      sourceGapIds: { type: 'array', items: { type: 'string' } },
      sourceConversationIds: { type: 'array', items: { type: 'string' } },
      relatedArticles: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            title: { type: 'string' },
            url: { type: ['string', 'null'] },
          },
          required: ['id', 'title'],
        },
      },
      generatedFromGapProposal: { type: 'boolean' },
    },
    required: ['articleId', 'projectId', 'title', 'content'],
  },
};

// ============================================================================
// apply_kb_article_diff
// ============================================================================

export interface ApplyKbArticleDiffInput {
  /** Markdown the diff was computed against — used to detect editor drift. */
  originalMarkdown: string;
  /** Proposed full-article markdown to render as a reviewable diff. */
  modifiedMarkdown: string;
  /** Short human-readable summary of the proposed edit. */
  summary?: string;
  /** Revision fingerprint of the editor at proposal time — for drift detection. */
  editorRevision?: string;
}

export const APPLY_KB_ARTICLE_DIFF_TOOL: FrontendToolDefinition<ApplyKbArticleDiffInput> = {
  name: 'apply_kb_article_diff',
  description:
    'Render a reviewable diff in the knowledge-base article editor. The user accepts or rejects in-editor. Only valid on a knowledge-article-edit page.',
  inputSchema: {
    type: 'object',
    properties: {
      originalMarkdown: { type: 'string', description: 'Markdown the diff was computed against.' },
      modifiedMarkdown: { type: 'string', description: 'Proposed full-article markdown.' },
      summary: { type: 'string', description: 'Short summary of the proposed edit.' },
      editorRevision: { type: 'string', description: 'Editor revision fingerprint at proposal time.' },
    },
    required: ['originalMarkdown', 'modifiedMarkdown'],
  },
};

// ============================================================================
// propose_playbook_tool_attachment
// ============================================================================

export interface ProposePlaybookToolAttachmentInput {
  proposals: Array<{
    capabilityKind: 'tool' | 'automation';
    capabilityId: string;
    /** Why the instructions need it, in the author's terms. */
    reason?: string;
  }>;
}

export const PROPOSE_PLAYBOOK_TOOL_ATTACHMENT_TOOL: FrontendToolDefinition<ProposePlaybookToolAttachmentInput> = {
  name: 'propose_playbook_tool_attachment',
  description:
    'Propose attaching tools or automations to the playbook being edited. The editor shows an Attach / Not now choice for each; nothing is attached until the user accepts and saves. Only valid on a playbook-edit page.',
  inputSchema: {
    type: 'object',
    properties: {
      proposals: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            capabilityKind: { type: 'string', enum: ['tool', 'automation'] },
            capabilityId: { type: 'string', description: 'The tool definition id or automation id.' },
            reason: { type: 'string', description: 'Why the instructions need it.' },
          },
          required: ['capabilityKind', 'capabilityId'],
        },
      },
    },
    required: ['proposals'],
  },
};

// ============================================================================
// dismiss_copilot_notifications
// ============================================================================

export interface DismissCopilotNotificationsInput {
  ids: string[];
  /** What the user declined, in a few words. */
  note?: string;
}

export const DISMISS_COPILOT_NOTIFICATIONS_TOOL: FrontendToolDefinition<DismissCopilotNotificationsInput> = {
  name: 'dismiss_copilot_notifications',
  description:
    'Record that the user declined Copilot notifications shown on this page, with a short note of what they declined.',
  inputSchema: {
    type: 'object',
    properties: {
      ids: { type: 'array', items: { type: 'string' }, description: 'The notification ids from page context.' },
      note: { type: 'string', description: 'What the user declined, in a few words.' },
    },
    required: ['ids'],
  },
};

// ============================================================================
// apply_playbook_instructions_edit
// ============================================================================

export interface ApplyPlaybookInstructionsEditInput {
  /** The instructions the proposal was written against; the editor refuses it if its text has moved on. */
  originalInstructions: string;
  /** The full revised instructions. */
  modifiedInstructions: string;
  /** Short summary of the change, for the review panel. */
  summary?: string;
}

export const APPLY_PLAYBOOK_INSTRUCTIONS_EDIT_TOOL: FrontendToolDefinition<ApplyPlaybookInstructionsEditInput> = {
  name: 'apply_playbook_instructions_edit',
  description:
    'Show a proposed rewrite of the playbook instructions in the editor for the author to accept or reject. Nothing changes until they accept, and the save makes it real. Only valid on a playbook-edit page.',
  inputSchema: {
    type: 'object',
    properties: {
      originalInstructions: { type: 'string', description: 'The instructions the proposal was written against.' },
      modifiedInstructions: { type: 'string', description: 'The full revised instructions.' },
      summary: { type: 'string', description: 'Short summary of the change.' },
    },
    required: ['originalInstructions', 'modifiedInstructions'],
  },
};
